const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;

/** Labels left behind when a watch URL is saved without video metadata. */
export function isGenericYouTubeLabel(value) {
  const text = String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!text) return true;
  return (
    text === 'watch'
    || text === 'youtube'
    || text === 'youtube video'
    || text === '- youtube'
    || text === 'video'
  );
}

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'youtu.be',
  'www.youtu.be',
]);

/**
 * Video id from a YouTube watch, share, shorts, live, or embed URL.
 * @param {string} input
 * @returns {string|null}
 */
export function parseYouTubeVideoId(input) {
  const raw = String(input || '').trim();
  if (!raw) return null;

  let parsed;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;

  const host = parsed.hostname.toLowerCase();
  if (!YOUTUBE_HOSTS.has(host)) return null;

  if (host === 'youtu.be' || host === 'www.youtu.be') {
    const id = parsed.pathname.split('/').filter(Boolean)[0] || '';
    return VIDEO_ID_RE.test(id) ? id : null;
  }

  const fromQuery = parsed.searchParams.get('v') || '';
  if (VIDEO_ID_RE.test(fromQuery)) return fromQuery;

  const parts = parsed.pathname.split('/').filter(Boolean);
  const marker = parts[0];
  const id = parts[1] || '';
  if (
    (marker === 'embed' || marker === 'shorts' || marker === 'live' || marker === 'v')
    && VIDEO_ID_RE.test(id)
  ) {
    return id;
  }

  return null;
}

/** Clock label for a saved playback second, or '' when there is nothing to show. */
export function formatYouTubeMoment(seconds) {
  const total = Math.floor(Number(seconds));
  if (!(total >= 1)) return '';
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = value => String(value).padStart(2, '0');
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(secs)}`;
  return `${minutes}:${pad(secs)}`;
}

/**
 * Saved playback second on the pin comment, or 0.
 * @param {object|null|undefined} activity
 * @returns {number}
 */
export function readYouTubeMomentSeconds(activity) {
  const comments = activity?.content?.[0]?.comments;
  const pinnedId = activity?.action?.commentId || activity?.commentId;
  let comment = null;
  if (Array.isArray(comments) && comments.length) {
    comment = (pinnedId && comments.find(item => String(item?._id) === String(pinnedId))) || comments[0];
  }
  const raw = comment?.highlightData?.youtubeMomentSeconds
    ?? activity?.action?.highlightData?.youtubeMomentSeconds;
  const seconds = Math.floor(Number(raw));
  return seconds >= 1 && seconds <= 86400 ? seconds : 0;
}

/**
 * @param {object|null|undefined} activity
 * @param {number} seconds
 * @returns {object|null|undefined}
 */
export function withYouTubeMoment(activity, seconds) {
  const saved = Math.floor(Number(seconds));
  if (!activity || !(saved >= 1) || saved > 86400) return activity;
  const apply = highlight => ({ ...(highlight || {}), youtubeMomentSeconds: saved });
  const content = Array.isArray(activity.content) ? activity.content : [];
  const content0 = content[0];
  const pinnedId = activity.action?.commentId || activity.commentId;
  const comments = Array.isArray(content0?.comments)
    ? content0.comments.map((comment, index) => {
      const isTarget = pinnedId
        ? String(comment?._id) === String(pinnedId)
        : index === 0;
      return isTarget
        ? { ...comment, highlightData: apply(comment.highlightData) }
        : comment;
    })
    : content0?.comments;
  return {
    ...activity,
    action: activity.action
      ? { ...activity.action, highlightData: apply(activity.action.highlightData) }
      : activity.action,
    content: content0
      ? [{ ...content0, comments }, ...content.slice(1)]
      : activity.content,
  };
}

/** True for a still this app stored for a YouTube moment. */
export function isYouTubeMomentImageUrl(url) {
  return /_yt\d+(?:_w\d+)?\.jpe?g(?:\?|#|$)/i.test(String(url || ''));
}

function clearMomentShot(target) {
  if (!target) return target;
  const urls = [
    target.elementScreenshotUrl,
    target.elementScreenshotThumbUrl,
    target.elementScreenshotMediumUrl,
  ];
  if (!urls.some(isYouTubeMomentImageUrl)) return target;
  return {
    ...target,
    elementScreenshotUrl: '',
    elementScreenshotThumbUrl: '',
    elementScreenshotMediumUrl: '',
  };
}

/**
 * Drop a stored moment still so the card falls back to the video thumbnail.
 * @param {object|null|undefined} activity
 */
export function withoutYouTubeMomentImage(activity) {
  if (!activity) return activity;
  const content = Array.isArray(activity.content) ? activity.content : [];
  const content0 = content[0];
  const pinnedId = activity.action?.commentId || activity.commentId;
  const comments = Array.isArray(content0?.comments)
    ? content0.comments.map((comment, index) => {
      const isTarget = pinnedId
        ? String(comment?._id) === String(pinnedId)
        : index === 0;
      return isTarget ? clearMomentShot(comment) : comment;
    })
    : content0?.comments;
  return {
    ...activity,
    action: clearMomentShot(activity.action),
    content: content0
      ? [{ ...content0, comments }, ...content.slice(1)]
      : activity.content,
  };
}

/**
 * Point the pin's stored still at a captured frame. The card prefers this over the
 * generic YouTube thumbnail.
 * @param {object|null|undefined} activity
 * @param {number} seconds
 * @param {string} imageUrl
 */
export function withYouTubeMomentImage(activity, seconds, imageUrl) {
  const next = withYouTubeMoment(activity, seconds);
  const url = String(imageUrl || '').trim();
  if (!next || !url) return next;
  const applyShot = target => ({
    ...target,
    elementScreenshotUrl: url,
    elementScreenshotThumbUrl: url,
    elementScreenshotMediumUrl: url,
  });
  const content = Array.isArray(next.content) ? next.content : [];
  const content0 = content[0];
  const pinnedId = next.action?.commentId || next.commentId;
  const comments = Array.isArray(content0?.comments)
    ? content0.comments.map((comment, index) => {
      const isTarget = pinnedId
        ? String(comment?._id) === String(pinnedId)
        : index === 0;
      return isTarget ? applyShot(comment) : comment;
    })
    : content0?.comments;
  return {
    ...next,
    action: next.action ? applyShot(next.action) : next.action,
    content: content0
      ? [{ ...content0, comments }, ...content.slice(1)]
      : next.content,
  };
}

function buildYouTubeEmbedUrl(videoId, startSeconds) {
  const params = new URLSearchParams({ autoplay: '1', rel: '0' });
  const start = Math.floor(Number(startSeconds) || 0);
  if (start >= 1) params.set('start', String(start));
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params}`;
}

/**
 * @param {string} href
 * @param {{ title?: string, startSeconds?: number }} [options]
 * @returns {{ videoId: string, href: string, title: string, thumbnailUrl: string, embedUrl: string, momentSeconds: number, momentLabel: string }|null}
 */
export function getYouTubeVideoFromHref(href, { title = '', startSeconds = 0 } = {}) {
  const videoId = parseYouTubeVideoId(href);
  if (!videoId) return null;
  const cleanTitle = String(title || '').trim() || 'YouTube video';
  const momentSeconds = Math.floor(Number(startSeconds) || 0);
  const savedSeconds = momentSeconds >= 1 && momentSeconds <= 86400 ? momentSeconds : 0;
  return {
    videoId,
    href: String(href || '').trim(),
    title: cleanTitle,
    thumbnailUrl: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
    embedUrl: buildYouTubeEmbedUrl(videoId, savedSeconds),
    momentSeconds: savedSeconds,
    momentLabel: formatYouTubeMoment(savedSeconds),
  };
}

/**
 * Page URL on an activity, including the optimistic paste card.
 * @param {object|null|undefined} activity
 * @returns {string}
 */
export function getActivityPageHref(activity) {
  return String(
    activity?.content?.[0]?.urls?.[0]?.href
    || activity?.pendingUrl
    || ''
  ).trim();
}

/**
 * @param {object|null|undefined} activity
 * @returns {{ videoId: string, href: string, title: string, thumbnailUrl: string, embedUrl: string, momentSeconds: number, momentLabel: string }|null}
 */
export function getYouTubeVideoFromActivity(activity) {
  const href = getActivityPageHref(activity);
  if (!href) return null;
  const rawTitle = String(activity?.content?.[0]?.title || '').trim();
  const host = String(
    activity?.content?.[0]?.urls?.[0]?.hostname
    || activity?.content?.[0]?.urls?.[0]?.host
    || ''
  ).replace(/^www\./i, '');
  const title = !rawTitle || rawTitle === host || isGenericYouTubeLabel(rawTitle)
    ? 'YouTube video'
    : rawTitle;
  return getYouTubeVideoFromHref(href, {
    title,
    startSeconds: readYouTubeMomentSeconds(activity),
  });
}
